import React, { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../store/slices/authSlice";
import type { AppDispatch, RootState } from "../../store";
import {
  HomeIcon,
  UserGroupIcon,
  TicketIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  BellIcon,
  DocumentTextIcon,
  CreditCardIcon,
  ChartBarIcon,
  ServerIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  KeyIcon,
  DocumentDuplicateIcon,
  ClipboardDocumentListIcon,
  LockClosedIcon,
  SparklesIcon,
  TagIcon,
  RectangleStackIcon,
  GlobeAltIcon,
  Square3Stack3DIcon,
  ShieldExclamationIcon,
} from "@heroicons/react/24/outline";

interface AdminLayoutProps {
  children?: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  const { user } = useSelector((state: RootState) => state.auth);

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
      navigate("/");
    } catch (error) {
      // Even if logout fails on backend, redirect to home
      navigate("/");
    }
  };

  // Admin navigation items
  const adminNavItems = [
    {
      name: "Dashboard",
      icon: HomeIcon,
      href: "/admin",
      current: location.pathname === "/admin",
    },
    {
      name: "Users",
      icon: UserGroupIcon,
      href: "/admin/users",
      current: location.pathname.includes("/admin/users"),
    },
    {
      name: "Creators",
      icon: UserGroupIcon,
      href: "/admin/creators",
      current: location.pathname.includes("/admin/creators"),
    },
    {
      name: "Subscriptions",
      icon: RectangleStackIcon,
      href: "/admin/subscriptions",
      current: location.pathname.includes("/admin/subscriptions"),
    },
    {
      name: "Tiers",
      icon: Square3Stack3DIcon,
      href: "/admin/tiers",
      current: location.pathname.includes("/admin/tiers"),
    },
    {
      name: "Posts",
      icon: DocumentTextIcon,
      href: "/admin/posts",
      current: location.pathname.includes("/admin/posts"),
    },
    {
      name: "Comments",
      icon: ChatBubbleLeftRightIcon,
      href: "/admin/comments",
      current: location.pathname.includes("/admin/comments"),
    },
    {
      name: "Messages",
      icon: EnvelopeIcon,
      href: "/admin/messages",
      current: location.pathname.includes("/admin/messages"),
    },
    {
      name: "Reports",
      icon: ExclamationTriangleIcon,
      href: "/admin/reports",
      current: location.pathname.includes("/admin/reports"),
    },
    {
      name: "Payments",
      icon: CreditCardIcon,
      href: "/admin/payments",
      current: location.pathname.includes("/admin/payments"),
    },
    {
      name: "Coupons",
      icon: TicketIcon,
      href: "/admin/coupons",
      current: location.pathname.includes("/admin/coupons"),
    },
    {
      name: "KYC",
      icon: ShieldCheckIcon,
      href: "/admin/kyc",
      current: location.pathname.includes("/admin/kyc"),
    },
    {
      name: "Payouts",
      icon: CurrencyDollarIcon,
      href: "/admin/payouts",
      current: location.pathname.includes("/admin/payouts"),
    },
    {
      name: "Taxes",
      icon: DocumentDuplicateIcon,
      href: "/admin/taxes",
      current: location.pathname.includes("/admin/taxes"),
    },
    {
      name: "Analytics",
      icon: ChartBarIcon,
      href: "/admin/analytics",
      current: location.pathname.includes("/admin/analytics"),
    },
    {
      name: "Notifications",
      icon: BellIcon,
      href: "/admin/notifications",
      current: location.pathname.includes("/admin/notifications"),
    },
    {
      name: "Email Templates",
      icon: EnvelopeIcon,
      href: "/admin/email-templates",
      current: location.pathname.includes("/admin/email-templates"),
    },
    {
      name: "Webhooks",
      icon: GlobeAltIcon,
      href: "/admin/webhooks",
      current: location.pathname.includes("/admin/webhooks"),
    },
    {
      name: "API Keys",
      icon: KeyIcon,
      href: "/admin/api-keys",
      current: location.pathname.includes("/admin/api-keys"),
    },
    {
      name: "Audit Logs",
      icon: ClipboardDocumentListIcon,
      href: "/admin/audit-logs",
      current: location.pathname.includes("/admin/audit-logs"),
    },
    {
      name: "GDPR Requests",
      icon: DocumentTextIcon,
      href: "/admin/gdpr-requests",
      current: location.pathname.includes("/admin/gdpr-requests"),
    },
    {
      name: "Compliance Review",
      icon: ShieldCheckIcon,
      href: "/admin/compliance-review",
      current: location.pathname.includes("/admin/compliance-review"),
    },
    {
      name: "Security",
      icon: LockClosedIcon,
      href: "/admin/security",
      current: location.pathname.includes("/admin/security"),
    },
    {
      name: "Features",
      icon: SparklesIcon,
      href: "/admin/features",
      current: location.pathname.includes("/admin/features"),
    },
    {
      name: "Tags",
      icon: TagIcon,
      href: "/admin/tags",
      current: location.pathname.includes("/admin/tags"),
    },
    {
      name: "Mass Actions",
      icon: ShieldExclamationIcon,
      href: "/admin/mass-actions",
      current: location.pathname.includes("/admin/mass-actions"),
    },
    {
      name: "Settings",
      icon: CogIcon,
      href: "/admin/settings",
      current: location.pathname.includes("/admin/settings"),
    },
    {
      name: "Branding",
      icon: CogIcon,
      href: "/admin/branding",
      current: location.pathname.includes("/admin/branding"),
    },
    {
      name: "System",
      icon: ServerIcon,
      href: "/admin/system",
      current: location.pathname.includes("/admin/system"),
    },
  ];

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--patreon-cream)' }}>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 bg-white text-black px-3 py-2 rounded shadow z-50"
      >
        Skip to main content
      </a>
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 flex lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
        </div>
      )}

      {/* Sidebar */}
      <div
        className={`
        fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out bg-[color:var(--patreon-card-bg)] shadow-[var(--patreon-card-shadow)]
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0 lg:relative
      `}
      >
        <div className="flex flex-col h-full">
          {/* Logo and close button */}
          <div className="flex items-center justify-between h-16 px-6 border-b" style={{ borderColor: 'var(--patreon-card-border)' }}>
            <div className="flex items-center">
              <div className="w-8 h-8 rounded-full mr-3" style={{ background: 'var(--patreon-btn-primary-bg)' }}></div>
              <span className="text-xl font-bold text-gray-900">Admin</span>
            </div>
            <button className="lg:hidden" onClick={() => setSidebarOpen(false)}>
              <XMarkIcon className="w-6 h-6 text-gray-500" />
            </button>
          </div>

          {/* User info */}
          <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--patreon-card-border)' }}>
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-r from-orange-400 to-pink-500 rounded-full flex items-center justify-center text-white font-semibold">
                {user?.firstName?.[0] || user?.email?.[0] || "A"}
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">
                  {user?.firstName && user?.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : user?.email || "Admin"}
                </p>
                <p className="text-xs text-gray-500">Administrator</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {adminNavItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.name}
                  onClick={() => {
                    navigate(item.href);
                    setSidebarOpen(false);
                  }}
                  className={`
                    w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-200
                    ${
                      item.current
                        ? "text-[color:var(--patreon-orange)] border-r-2 bg-[color:var(--patreon-card-bg)]"
                        : "text-gray-600 hover:bg-[color:var(--patreon-card-bg)] hover:text-gray-900"
                    }
                  `}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.name}
                </button>
              );
            })}
          </nav>

          {/* User menu */}
          <div className="border-t border-gray-200 p-4">
            <button
              onClick={() => navigate("/dashboard")}
              className="w-full flex items-center px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-lg transition-colors duration-200 mb-2"
            >
              <HomeIcon className="w-5 h-5 mr-3" />
              Back to Dashboard
            </button>
            <button
              onClick={() => navigate("/dashboard/settings")}
              className="w-full flex items-center px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-lg transition-colors duration-200 mb-2"
            >
              <CogIcon className="w-5 h-5 mr-3" />
              Settings
            </button>
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5 mr-3" />
              Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col">
        {/* Top navigation bar */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              {/* Left side - Mobile menu button or empty space */}
              <div className="flex items-center">
                <button
                  className="lg:hidden"
                  onClick={() => setSidebarOpen(true)}
                >
                  <Bars3Icon className="w-6 h-6 text-gray-500" />
                </button>
                {/* Desktop: empty space to push right items to the right */}
                <div className="hidden lg:block"></div>
              </div>

              {/* Right side buttons */}
              <div className="flex items-center space-x-4 ml-auto">
                {/* Notifications */}
                <div className="relative">
                  <button 
                    onClick={() => setNotificationsOpen(!notificationsOpen)}
                    className="p-2 text-gray-400 hover:text-gray-500 relative rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <BellIcon className="w-6 h-6" />
                    <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                  </button>

                  {/* Notifications Dropdown */}
                  {notificationsOpen && (
                    <>
                      <div 
                        className="fixed inset-0 z-10" 
                        onClick={() => setNotificationsOpen(false)}
                      />
                      <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                        <div className="p-4 border-b border-gray-200">
                          <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
                        </div>
                        <div className="max-h-96 overflow-y-auto">
                          <div className="p-4 text-center text-sm text-gray-500">
                            No new notifications
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* User menu */}
                <div className="relative">
                  <button 
                    onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                    className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-8 h-8 bg-gradient-to-r from-orange-400 to-pink-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                      {user?.firstName?.[0] || user?.email?.[0] || "A"}
                    </div>
                    <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                  </button>

                  {/* Profile Dropdown */}
                  {profileMenuOpen && (
                    <>
                      <div 
                        className="fixed inset-0 z-10" 
                        onClick={() => setProfileMenuOpen(false)}
                      />
                      <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                        <div className="p-4 border-b border-gray-200">
                          <p className="text-sm font-medium text-gray-900">
                            {user?.firstName && user?.lastName
                              ? `${user.firstName} ${user.lastName}`
                              : user?.email || "Admin"}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">{user?.email}</p>
                        </div>
                        <div className="py-2">
                          <button
                            onClick={() => {
                              setProfileMenuOpen(false);
                              navigate("/dashboard");
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                          >
                            <HomeIcon className="w-4 h-4 mr-3" />
                            Back to Dashboard
                          </button>
                          <button
                            onClick={() => {
                              setProfileMenuOpen(false);
                              navigate("/dashboard/settings");
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                          >
                            <CogIcon className="w-4 h-4 mr-3" />
                            Settings
                          </button>
                        </div>
                        <div className="border-t border-gray-200 py-2">
                          <button
                            onClick={() => {
                              setProfileMenuOpen(false);
                              handleLogout();
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
                          >
                            <ArrowRightOnRectangleIcon className="w-4 h-4 mr-3" />
                            Sign out
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main id="main-content" className="flex-1 overflow-x-hidden" tabIndex={-1}>
          {children || <Outlet />}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;




